import type {
  ClassTypeDTO,
  CreateClassTypeDTO,
  CreateInstructorDTO,
  CreateLevelDTO,
  CreateRoomDTO,
  CreateStudentGroupDTO,
  InstructorCheckInDTO,
  InstructorClassSessionDTO,
  InstructorDTO,
  InstructorPrivateLessonDTO,
  InstructorSummaryDTO,
  LevelDTO,
  ReorderLevelsDTO,
  ReorderStudentGroupsDTO,
  RoomDTO,
  StudentGroupDTO,
  UpdateClassTypeDTO,
  UpdateInstructorDTO,
  UpdateLevelDTO,
  UpdateRoomDTO,
  UpdateStudentGroupDTO,
} from "@/modules/master/domain/contracts";
import type {
  OffsetPageDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export const masterApi = {
  // Class Types
  classTypes: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<ClassTypeDTO>> {
      const url = new URL(`${config.apiUrl}/master/class-types`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<ClassTypeDTO>>(url.toString());
    },
    async get(id: string): Promise<ClassTypeDTO> {
      return fetcher.get<ClassTypeDTO>(
        `${config.apiUrl}/master/class-types/${id}`,
      );
    },
    async create(input: CreateClassTypeDTO): Promise<ClassTypeDTO> {
      return fetcher.post<ClassTypeDTO>(
        `${config.apiUrl}/master/class-types`,
        input,
      );
    },
    async update(id: string, input: UpdateClassTypeDTO): Promise<ClassTypeDTO> {
      return fetcher.put<ClassTypeDTO>(
        `${config.apiUrl}/master/class-types/${id}`,
        input,
      );
    },
    async remove(id: string): Promise<{ id: string }> {
      return fetcher.delete<{ id: string }>(
        `${config.apiUrl}/master/class-types/${id}`,
      );
    },
  },

  // Levels
  levels: {
    async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<LevelDTO>> {
      const url = new URL(`${config.apiUrl}/master/levels`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<LevelDTO>>(url.toString());
    },
    async get(id: string): Promise<LevelDTO> {
      return fetcher.get<LevelDTO>(`${config.apiUrl}/master/levels/${id}`);
    },
    async create(input: CreateLevelDTO): Promise<LevelDTO> {
      return fetcher.post<LevelDTO>(`${config.apiUrl}/master/levels`, input);
    },
    async update(id: string, input: UpdateLevelDTO): Promise<LevelDTO> {
      return fetcher.put<LevelDTO>(
        `${config.apiUrl}/master/levels/${id}`,
        input,
      );
    },
    async reorder(input: ReorderLevelsDTO): Promise<LevelDTO[]> {
      return fetcher.put<LevelDTO[]>(
        `${config.apiUrl}/master/levels/reorder`,
        input,
      );
    },
    async remove(id: string): Promise<{ id: string }> {
      return fetcher.delete<{ id: string }>(
        `${config.apiUrl}/master/levels/${id}`,
      );
    },
  },

  // Rooms
  rooms: {
    async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<RoomDTO>> {
      const url = new URL(`${config.apiUrl}/master/rooms`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<RoomDTO>>(url.toString());
    },
    async get(id: string): Promise<RoomDTO> {
      return fetcher.get<RoomDTO>(`${config.apiUrl}/master/rooms/${id}`);
    },
    async create(input: CreateRoomDTO): Promise<RoomDTO> {
      return fetcher.post<RoomDTO>(`${config.apiUrl}/master/rooms`, input);
    },
    async update(id: string, input: UpdateRoomDTO): Promise<RoomDTO> {
      return fetcher.put<RoomDTO>(`${config.apiUrl}/master/rooms/${id}`, input);
    },
    async remove(id: string): Promise<{ id: string }> {
      return fetcher.delete<{ id: string }>(
        `${config.apiUrl}/master/rooms/${id}`,
      );
    },
  },

  // Instructors
  instructors: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<InstructorDTO>> {
      const url = new URL(`${config.apiUrl}/master/instructors`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<InstructorDTO>>(url.toString());
    },
    async get(id: string): Promise<InstructorDTO> {
      return fetcher.get<InstructorDTO>(
        `${config.apiUrl}/master/instructors/${id}`,
      );
    },
    async create(input: CreateInstructorDTO): Promise<InstructorDTO> {
      return fetcher.post<InstructorDTO>(
        `${config.apiUrl}/master/instructors`,
        input,
      );
    },
    async update(
      id: string,
      input: UpdateInstructorDTO,
    ): Promise<InstructorDTO> {
      return fetcher.put<InstructorDTO>(
        `${config.apiUrl}/master/instructors/${id}`,
        input,
      );
    },
    async remove(id: string): Promise<{ id: string }> {
      return fetcher.delete<{ id: string }>(
        `${config.apiUrl}/master/instructors/${id}`,
      );
    },
    async getSummary(id: string): Promise<InstructorSummaryDTO> {
      return fetcher.get<InstructorSummaryDTO>(
        `${config.apiUrl}/master/instructors/${id}/summary`,
      );
    },
    async listClassSessions(
      id: string,
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<InstructorClassSessionDTO>> {
      const url = new URL(
        `${config.apiUrl}/master/instructors/${id}/class-sessions`,
      );
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<InstructorClassSessionDTO>>(
        url.toString(),
      );
    },
    async listPrivateLessons(
      id: string,
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<InstructorPrivateLessonDTO>> {
      const url = new URL(
        `${config.apiUrl}/master/instructors/${id}/private-lessons`,
      );
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<InstructorPrivateLessonDTO>>(
        url.toString(),
      );
    },
    async listCheckIns(
      id: string,
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<InstructorCheckInDTO>> {
      const url = new URL(
        `${config.apiUrl}/master/instructors/${id}/check-ins`,
      );
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<InstructorCheckInDTO>>(
        url.toString(),
      );
    },
  },

  // Student Groups
  studentGroups: {
    async list(
      query: OffsetPageQueryDTO,
    ): Promise<OffsetPageDTO<StudentGroupDTO>> {
      const url = new URL(`${config.apiUrl}/master/student-groups`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<StudentGroupDTO>>(url.toString());
    },
    async get(id: string): Promise<StudentGroupDTO> {
      return fetcher.get<StudentGroupDTO>(
        `${config.apiUrl}/master/student-groups/${id}`,
      );
    },
    async create(input: CreateStudentGroupDTO): Promise<StudentGroupDTO> {
      return fetcher.post<StudentGroupDTO>(
        `${config.apiUrl}/master/student-groups`,
        input,
      );
    },
    async update(
      id: string,
      input: UpdateStudentGroupDTO,
    ): Promise<StudentGroupDTO> {
      return fetcher.put<StudentGroupDTO>(
        `${config.apiUrl}/master/student-groups/${id}`,
        input,
      );
    },
    async reorder(input: ReorderStudentGroupsDTO): Promise<StudentGroupDTO[]> {
      return fetcher.put<StudentGroupDTO[]>(
        `${config.apiUrl}/master/student-groups/reorder`,
        input,
      );
    },
    async remove(id: string): Promise<{ id: string }> {
      return fetcher.delete<{ id: string }>(
        `${config.apiUrl}/master/student-groups/${id}`,
      );
    },
  },
};
